/**
 * CommissionTab (Tab 8) — Split-panel commission plan management.
 *
 * KOPS-465: full rebuild from the placeholder landing card. Now a
 * proper split-panel layout matching the rest of the Broker
 * Intelligence Hub:
 *
 *   Left (380px)  — list of commission plan templates with search,
 *                   agent-count badge, and "Create Plan" inline form
 *   Right (flex-1) — selected template detail with editable
 *                   Structure (splits / fees) + Agents Assigned list
 *
 * Backend endpoints (all exist, no new work):
 *   GET    /commission-plans/templates/:brokerageId
 *   POST   /commission-plans/templates
 *   PUT    /commission-plans/templates/:templateId
 *   GET    /commission-plans/agent-plans/:brokerageId
 *   POST   /commission-plans/assign
 */

import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHandHoldingDollar,
  faPercent,
  faUsers,
  faPlus,
  faSearch,
  faFloppyDisk,
  faEdit,
  faTimes,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';

import { colors, fonts, SplitPanel, GoldButton, OutlineButton } from '../shared';
import axiosInstance from '../../../helpers/api';

interface CommissionTabProps {
  brokerageId?: string;
}

interface Template {
  id: string;
  name: string;
  description: string;
  baseAgentSplit: number;
  baseBrokerSplit: number;
  transactionFee: number;
  eoFee: number;
  platformMonthlyFee: number;
  monthlyFee: number;
  isDefault: boolean;
  isActive: boolean;
  agentCount: number;
}

interface AgentPlan {
  agentId: string;
  agentName: string;
  email: string;
  currentPlanId: string | null;
  currentPlanName: string | null;
  status: string;
}

const CommissionTab: React.FC<CommissionTabProps> = ({ brokerageId }) => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Template | null>(null);
  const [search, setSearch] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    templateName: '',
    description: '',
    agentSplit: 80,
    brokerSplit: 20,
    transactionFee: 0,
    eoFee: 0,
    monthlyFee: 70,
  });
  const [creating, setCreating] = useState(false);

  // Edit form
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<typeof createForm & { id: string }>({
    id: '',
    templateName: '',
    description: '',
    agentSplit: 80,
    brokerSplit: 20,
    transactionFee: 0,
    eoFee: 0,
    monthlyFee: 70,
  });
  const [saving, setSaving] = useState(false);

  // Agent assignments for selected template
  const [agentPlans, setAgentPlans] = useState<AgentPlan[]>([]);
  const [agentPlansLoading, setAgentPlansLoading] = useState(false);

  // Alerts
  const [alert, setAlert] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    if (!brokerageId) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/commission-plans/templates/${brokerageId}`);
      if (res.data?.success) {
        setTemplates(res.data.templates || []);
      }
    } catch (err) {
      console.error('Failed to load commission templates:', err);
    } finally {
      setLoading(false);
    }
  }, [brokerageId]);

  const fetchAgentPlans = useCallback(async () => {
    if (!brokerageId) return;
    setAgentPlansLoading(true);
    try {
      const res = await axiosInstance.get(`/commission-plans/agents/${brokerageId}`);
      if (res.data?.success) {
        setAgentPlans(res.data.agentPlans || []);
      }
    } catch {
      setAgentPlans([]);
    } finally {
      setAgentPlansLoading(false);
    }
  }, [brokerageId]);

  useEffect(() => {
    fetchTemplates();
    fetchAgentPlans();
  }, [fetchTemplates, fetchAgentPlans]);

  const filtered = templates.filter(
    (t) =>
      !search ||
      t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = async () => {
    if (!createForm.templateName.trim()) {
      setAlert('❌ Plan name is required');
      setTimeout(() => setAlert(null), 3000);
      return;
    }
    setCreating(true);
    try {
      await axiosInstance.post('/commission-plans/templates', {
        brokerageId,
        templateName: createForm.templateName,
        description: createForm.description,
        baseRates: {
          agentSplit: createForm.agentSplit,
          brokerSplit: createForm.brokerSplit,
          transactionFee: createForm.transactionFee,
          eoFee: createForm.eoFee,
          agentMonthlyFee: createForm.monthlyFee,
        },
      });
      setAlert('✅ Commission plan created');
      setShowCreate(false);
      setCreateForm({ templateName: '', description: '', agentSplit: 80, brokerSplit: 20, transactionFee: 0, eoFee: 0, monthlyFee: 70 });
      await fetchTemplates();
    } catch (err: any) {
      setAlert(`❌ ${err?.response?.data?.message || err?.message || 'Failed to create plan'}`);
    } finally {
      setCreating(false);
      setTimeout(() => setAlert(null), 4000);
    }
  };

  const startEditing = (t: Template) => {
    setEditForm({
      id: t.id,
      templateName: t.name,
      description: t.description,
      agentSplit: t.baseAgentSplit,
      brokerSplit: t.baseBrokerSplit,
      transactionFee: t.transactionFee,
      eoFee: t.eoFee,
      monthlyFee: t.monthlyFee,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axiosInstance.put(`/commission-plans/templates/${editForm.id}`, {
        templateName: editForm.templateName,
        description: editForm.description,
        baseRates: {
          agentSplit: editForm.agentSplit,
          brokerSplit: editForm.brokerSplit,
          transactionFee: editForm.transactionFee,
          eoFee: editForm.eoFee,
          agentMonthlyFee: editForm.monthlyFee,
        },
      });
      setAlert('✅ Commission plan updated');
      setEditing(false);
      await fetchTemplates();
      // Keep the selected row fresh
      if (selected?.id === editForm.id) {
        setSelected((prev) =>
          prev
            ? {
                ...prev,
                name: editForm.templateName,
                description: editForm.description,
                baseAgentSplit: editForm.agentSplit,
                baseBrokerSplit: editForm.brokerSplit,
                transactionFee: editForm.transactionFee,
                eoFee: editForm.eoFee,
                monthlyFee: editForm.monthlyFee,
              }
            : prev
        );
      }
    } catch (err: any) {
      setAlert(`❌ ${err?.response?.data?.message || 'Failed to update plan'}`);
    } finally {
      setSaving(false);
      setTimeout(() => setAlert(null), 4000);
    }
  };

  // Agents assigned to the currently selected template
  const assignedAgents = selected
    ? agentPlans.filter((ap) => ap.currentPlanId === selected.id)
    : [];

  // Detail tab state
  const [detailTab, setDetailTab] = useState<'structure' | 'agents'>('structure');

  // Reset detail tab on template change
  useEffect(() => {
    setEditing(false);
    setDetailTab('structure');
  }, [selected?.id]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {alert && (
        <div
          style={{
            background: alert.startsWith('✅') ? '#d4edda' : '#f8d7da',
            color: alert.startsWith('✅') ? colors.green : colors.red,
            padding: '10px 24px',
            fontSize: '12px',
            fontWeight: 600,
          }}
        >
          {alert}
        </div>
      )}

      <SplitPanel
        left={
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Search + create */}
            <div style={{ padding: '16px 16px 12px', borderBottom: `1px solid ${colors.border}` }}>
              <div style={{ position: 'relative', marginBottom: '10px' }}>
                <FontAwesomeIcon
                  icon={faSearch}
                  style={{ position: 'absolute', left: '12px', top: '10px', fontSize: '12px', color: colors.muted }}
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search plans…"
                  style={{
                    width: '100%',
                    background: colors.white,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    padding: '8px 12px 8px 32px',
                    fontSize: '12px',
                    color: colors.navy,
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <GoldButton
                onClick={() => setShowCreate(!showCreate)}
                style={{ width: '100%', fontSize: '12px', padding: '8px' }}
              >
                <FontAwesomeIcon icon={showCreate ? faTimes : faPlus} />{' '}
                {showCreate ? 'Cancel' : 'Create Plan'}
              </GoldButton>
            </div>

            {/* Inline create form */}
            {showCreate && (
              <div
                style={{
                  padding: '14px 16px',
                  background: colors.offBg,
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <div style={{ marginBottom: '8px' }}>
                  <Label>Plan Name *</Label>
                  <Input
                    value={createForm.templateName}
                    onChange={(e) => setCreateForm({ ...createForm, templateName: e.target.value })}
                    placeholder="e.g. Stability Plan"
                  />
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <Label>Description</Label>
                  <Input
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
                  <div>
                    <Label>Agent Split (%)</Label>
                    <Input
                      type="number"
                      value={createForm.agentSplit}
                      onChange={(e) => setCreateForm({ ...createForm, agentSplit: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Broker Split (%)</Label>
                    <Input
                      type="number"
                      value={createForm.brokerSplit}
                      onChange={(e) => setCreateForm({ ...createForm, brokerSplit: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '10px' }}>
                  <div>
                    <Label>Txn Fee ($)</Label>
                    <Input
                      type="number"
                      value={createForm.transactionFee}
                      onChange={(e) => setCreateForm({ ...createForm, transactionFee: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>E&O Fee ($)</Label>
                    <Input
                      type="number"
                      value={createForm.eoFee}
                      onChange={(e) => setCreateForm({ ...createForm, eoFee: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Monthly ($)</Label>
                    <Input
                      type="number"
                      value={createForm.monthlyFee}
                      onChange={(e) => setCreateForm({ ...createForm, monthlyFee: Number(e.target.value) })}
                    />
                  </div>
                </div>
                <GoldButton onClick={handleCreate} disabled={creating} style={{ width: '100%', fontSize: '12px' }}>
                  {creating ? 'Creating…' : 'Create Commission Plan'}
                </GoldButton>
              </div>
            )}

            {/* Plan list */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>
                  Loading plans…
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: colors.muted, fontSize: '12px' }}>
                  {search ? 'No matching plans' : 'No commission plans yet. Click Create Plan above.'}
                </div>
              ) : (
                filtered.map((t) => {
                  const isSelected = selected?.id === t.id;
                  return (
                    <div
                      key={t.id}
                      onClick={() => setSelected(t)}
                      style={{
                        padding: '14px 16px',
                        cursor: 'pointer',
                        borderBottom: `1px solid ${colors.border}`,
                        borderLeft: isSelected ? `3px solid ${colors.gold}` : '3px solid transparent',
                        background: isSelected ? colors.offBg : 'transparent',
                        transition: 'background 0.1s',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: colors.navy }}>
                          {t.name}
                          {t.isDefault && (
                            <span
                              style={{
                                marginLeft: '8px',
                                background: `${colors.gold}22`,
                                color: colors.gold,
                                fontSize: '9px',
                                fontWeight: 700,
                                padding: '2px 6px',
                                borderRadius: '4px',
                                textTransform: 'uppercase',
                              }}
                            >
                              Default
                            </span>
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: '10px',
                            fontWeight: 600,
                            color: colors.muted,
                          }}
                        >
                          {t.agentCount} agent{t.agentCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div style={{ fontSize: '11px', color: colors.muted, marginTop: '4px' }}>
                        {t.baseAgentSplit}% / {t.baseBrokerSplit}%
                        {t.transactionFee ? ` · Txn $${t.transactionFee}` : ''}
                        {t.eoFee ? ` · E&O $${t.eoFee}` : ''}
                        {t.monthlyFee ? ` · $${t.monthlyFee}/mo` : ''}
                      </div>
                      {t.description && (
                        <div
                          style={{
                            fontSize: '11px',
                            color: colors.muted,
                            marginTop: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {t.description}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        }
        right={
          selected ? (
            <div style={{ padding: '24px', overflowY: 'auto', height: '100%' }}>
              {/* Profile header */}
              <div
                style={{
                  background: '#f8f9fa',
                  borderLeft: `4px solid ${colors.gold}`,
                  borderRadius: '8px',
                  padding: '20px 24px',
                  marginBottom: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <h2
                    style={{
                      fontFamily: fonts.display,
                      fontSize: '28px',
                      color: colors.navy,
                      margin: '0 0 4px',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {selected.name}
                  </h2>
                  {selected.description && (
                    <div style={{ fontSize: '13px', color: colors.muted }}>{selected.description}</div>
                  )}
                  <div style={{ fontSize: '12px', color: colors.muted, marginTop: '6px' }}>
                    <FontAwesomeIcon icon={faUsers} style={{ marginRight: '4px' }} />
                    {selected.agentCount} agent{selected.agentCount !== 1 ? 's' : ''} assigned
                  </div>
                </div>
                <OutlineButton onClick={() => startEditing(selected)} style={{ fontSize: '12px' }}>
                  <FontAwesomeIcon icon={faEdit} /> Edit
                </OutlineButton>
              </div>

              {/* Detail tabs */}
              <div
                style={{
                  display: 'flex',
                  gap: '0',
                  borderBottom: `2px solid ${colors.border}`,
                  marginBottom: '16px',
                }}
              >
                {([
                  { key: 'structure', label: 'Structure', icon: faPercent },
                  { key: 'agents', label: 'Agents Assigned', icon: faUsers },
                ] as const).map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setDetailTab(tab.key)}
                    style={{
                      background: 'none',
                      border: 'none',
                      borderBottom: `2px solid ${detailTab === tab.key ? colors.gold : 'transparent'}`,
                      color: detailTab === tab.key ? colors.navy : colors.muted,
                      fontWeight: detailTab === tab.key ? 700 : 500,
                      fontSize: '12px',
                      padding: '10px 18px',
                      cursor: 'pointer',
                      marginBottom: '-2px',
                      fontFamily: 'inherit',
                    }}
                  >
                    <FontAwesomeIcon icon={tab.icon} style={{ marginRight: '6px' }} />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Structure tab */}
              {detailTab === 'structure' && !editing && (
                <div
                  style={{
                    background: colors.white,
                    border: `1px solid ${colors.border}`,
                    borderLeft: `4px solid ${colors.gold}`,
                    borderRadius: '8px',
                    padding: '20px',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '16px',
                    }}
                  >
                    <MetricCard label="Agent Split" value={`${selected.baseAgentSplit}%`} color={colors.green} />
                    <MetricCard label="Broker Split" value={`${selected.baseBrokerSplit}%`} color={colors.gold} />
                    <MetricCard label="Transaction Fee" value={`$${selected.transactionFee}`} color={colors.blue} />
                    <MetricCard label="E&O Fee" value={`$${selected.eoFee}`} color={colors.orange} />
                    <MetricCard label="Monthly Fee" value={`$${selected.monthlyFee}`} color={colors.muted} />
                  </div>
                </div>
              )}

              {/* Structure edit mode */}
              {detailTab === 'structure' && editing && (
                <div
                  style={{
                    background: colors.white,
                    border: `1px solid ${colors.gold}`,
                    borderLeft: `4px solid ${colors.gold}`,
                    borderRadius: '8px',
                    padding: '20px',
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <Label>Plan Name</Label>
                    <Input
                      value={editForm.templateName}
                      onChange={(e) => setEditForm({ ...editForm, templateName: e.target.value })}
                    />
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <Label>Description</Label>
                    <Input
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    />
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                      gap: '12px',
                      marginBottom: '16px',
                    }}
                  >
                    <div>
                      <Label>Agent Split (%)</Label>
                      <Input
                        type="number"
                        value={editForm.agentSplit}
                        onChange={(e) => setEditForm({ ...editForm, agentSplit: Number(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Broker Split (%)</Label>
                      <Input
                        type="number"
                        value={editForm.brokerSplit}
                        onChange={(e) => setEditForm({ ...editForm, brokerSplit: Number(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Transaction Fee ($)</Label>
                      <Input
                        type="number"
                        value={editForm.transactionFee}
                        onChange={(e) => setEditForm({ ...editForm, transactionFee: Number(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>E&O Fee ($)</Label>
                      <Input
                        type="number"
                        value={editForm.eoFee}
                        onChange={(e) => setEditForm({ ...editForm, eoFee: Number(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label>Monthly Fee ($)</Label>
                      <Input
                        type="number"
                        value={editForm.monthlyFee}
                        onChange={(e) => setEditForm({ ...editForm, monthlyFee: Number(e.target.value) })}
                      />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <OutlineButton onClick={() => setEditing(false)} disabled={saving}>
                      Cancel
                    </OutlineButton>
                    <GoldButton onClick={handleSave} disabled={saving}>
                      <FontAwesomeIcon icon={faFloppyDisk} /> {saving ? 'Saving…' : 'Save Changes'}
                    </GoldButton>
                  </div>
                </div>
              )}

              {/* Agents tab */}
              {detailTab === 'agents' && (
                <div
                  style={{
                    background: colors.white,
                    border: `1px solid ${colors.border}`,
                    borderLeft: `4px solid ${colors.gold}`,
                    borderRadius: '8px',
                    padding: '20px',
                  }}
                >
                  {agentPlansLoading ? (
                    <div style={{ color: colors.muted, fontSize: '12px', textAlign: 'center', padding: '20px' }}>
                      Loading agents…
                    </div>
                  ) : assignedAgents.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: colors.muted }}>
                      <FontAwesomeIcon icon={faUsers} style={{ fontSize: '28px', marginBottom: '8px', color: colors.gold }} />
                      <div style={{ fontSize: '13px' }}>
                        No agents assigned to this plan yet.
                      </div>
                      <div style={{ fontSize: '11px', marginTop: '4px' }}>
                        Assign agents from the <strong>Agents</strong> tab → select agent → <strong>Commission</strong> sub-tab
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {assignedAgents.map((ap) => (
                        <div
                          key={ap.agentId}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '10px 14px',
                            background: colors.offBg,
                            borderRadius: '6px',
                            border: `1px solid ${colors.border}`,
                          }}
                        >
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: colors.navy }}>
                              {ap.agentName || '(unnamed)'}
                            </div>
                            <div style={{ fontSize: '11px', color: colors.muted }}>{ap.email || '—'}</div>
                          </div>
                          <span
                            style={{
                              fontSize: '10px',
                              fontWeight: 700,
                              color: colors.green,
                              background: `${colors.green}15`,
                              padding: '3px 8px',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                            }}
                          >
                            <FontAwesomeIcon icon={faCheckCircle} /> Active
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '48px',
                color: colors.muted,
              }}
            >
              <FontAwesomeIcon
                icon={faHandHoldingDollar}
                style={{ fontSize: '48px', marginBottom: '12px', color: colors.gold }}
              />
              <div style={{ fontSize: '15px', fontWeight: 600, color: colors.navy }}>
                Select a commission plan
              </div>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                Pick a plan from the left to view or edit its structure and agent assignments
              </div>
            </div>
          )
        }
      />
    </div>
  );
};

// ---- Small helpers ----

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      fontSize: '10px',
      color: colors.muted,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      fontWeight: 700,
      marginBottom: '4px',
    }}
  >
    {children}
  </div>
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input
    {...props}
    style={{
      width: '100%',
      background: colors.white,
      border: `1px solid ${colors.border}`,
      borderRadius: '6px',
      padding: '8px 10px',
      fontSize: '13px',
      color: colors.navy,
      outline: 'none',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      ...(props.style || {}),
    }}
  />
);

const MetricCard: React.FC<{ label: string; value: string; color: string }> = ({
  label,
  value,
  color,
}) => (
  <div
    style={{
      background: colors.offBg,
      borderRadius: '8px',
      padding: '14px 16px',
      borderLeft: `3px solid ${color}`,
    }}
  >
    <div style={{ fontSize: '10px', color: colors.muted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {label}
    </div>
    <div style={{ fontSize: '20px', fontWeight: 700, color: colors.navy, marginTop: '4px' }}>
      {value}
    </div>
  </div>
);

export default CommissionTab;
